import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import MetricCard from '@/components/apex/MetricCard';
import TimelineBar from '@/components/apex/TimelineBar';
import GovernanceStatement from '@/components/apex/GovernanceStatement';
import RecordDrawer from '@/components/apex/RecordDrawer';
import GovernanceGraphFull from '@/components/apex/GovernanceGraphFull';
import { SkeletonCard } from '@/components/apex/Skeleton';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Activity, GitGraph, ArrowRight, X, Play } from 'lucide-react';
import SeverityBadge from '@/components/apex/SeverityBadge';
import EvidenceFabricHealth from '@/components/apex/EvidenceFabricHealth';
import GovernanceConstraintPanel from '@/components/apex/GovernanceConstraintPanel';
import HowCalculated from '@/components/apex/HowCalculated';
import InvestigationBadge from '@/components/apex/InvestigationBadge';

const FRAMEWORK_COLORS = {
  'SOC 2': 'border-l-indigo-400',
  'ISO 27001': 'border-l-violet-400',
  'DORA': 'border-l-blue-400',
  'PCI DSS': 'border-l-emerald-400',
};

export default function CommandCenter() {
  const { seed, loading, role } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [graphOpen, setGraphOpen] = useState(false);
  const patternDrawer = params.get('pattern');

  // Esc to close governance graph modal
  useEffect(() => {
    if (!graphOpen) return;
    const handleEsc = (e) => { if (e.key === 'Escape') setGraphOpen(false); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [graphOpen]);
  const setPatternDrawer = (key) => {
    const next = new URLSearchParams(params);
    if (key) next.set('pattern', key);
    else next.delete('pattern');
    setParams(next, { replace: false });
  };

  if (loading || !seed) return <Loading />;

  const ps = seed.platform_status;

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="command-center">
      {/* Active breach banner — INV-2026-042 */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
        data-testid="active-breach-banner"
      >
        <div className="flex items-center gap-2 shrink-0">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
          </span>
          <span className="text-[11px] font-bold text-red-700 uppercase tracking-wider">Active governance breach</span>
        </div>
        <div className="text-[12px] text-red-700 flex-1 min-w-[240px]">
          <span className="font-semibold">INV-2026-042</span> — Dev-origin container in production payments service · PCI data exposed · Trust path traversed twice at 02:17 UTC
        </div>
        <button
          onClick={() => navigate('/lens/investigations?id=INV-2026-042&tab=replay')}
          data-testid="banner-open-investigation"
          className="shrink-0 text-[11px] font-bold text-red-700 hover:text-red-900 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-300 hover:bg-red-100 transition-colors"
        >
          <Play className="w-3 h-3" />
          Watch replay
        </button>
        <button
          onClick={() => navigate('/lens/remediation')}
          data-testid="banner-remediate"
          className="shrink-0 text-[11px] font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-md transition-colors"
        >
          Remediate →
        </button>
      </motion.div>

      {/* Evidence Fabric Health strip */}
      <EvidenceFabricHealth />

      {/* Governance Invariants — structural laws, not findings */}
      <GovernanceConstraintPanel navigate={navigate} />

      {/* Title */}
      <div>
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Command Center</span>
          <RoleViewLabel role={role} />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Stratum Financial Group</h1>
        <p className="text-sm text-apex-neutral mt-1">Governance posture across {ps.accounts_monitored} accounts · 14 months of evidence</p>
      </div>

      {/* Metrics — order varies by role */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className={metricOrder(role, 'readiness')} data-metric="readiness">
          <MetricCard
            testId="metric-readiness"
            label="Evidence Readiness"
            value={ps.evidence_readiness}
            suffix="%"
            tone="teal"
            trend={ps.evidence_readiness_trend}
            trendLabel={`+${ps.evidence_readiness_trend}% from last month`}
            methodology="Calculated from: % of controls with continuous coverage (40%) + % of approved changes with baseline (35%) + % of exposure windows with documentation (25%). Based on 14 months of CloudTrail history. 98.7% event coverage."
          />
        </div>
        <div className={metricOrder(role, 'gaps')} data-metric="gaps">
          <MetricCard
            testId="metric-gaps"
            label="Active Control Gaps"
            value={ps.active_gaps}
            tone="amber"
            trend={0}
            trendLabel="2 critical · 1 high"
            methodology="Counts every control whose enforcement coverage dropped below baseline at least once in the past 30 days, not yet remediated. Each gap traces to specific Config or SCP evidence in CloudTrail."
            onClick={() => navigate('/lens/environment')}
          />
        </div>
        <div className={metricOrder(role, 'changes')} data-metric="changes">
          <MetricCard
            testId="metric-changes"
            label="Unauthorized Changes"
            value={ps.unauthorized_changes}
            tone="red"
            trend={-1}
            trendLabel="Last 30 days · 2 pipeline bypasses"
            methodology="Counts production changes where actor or pipeline path does not match the approved baseline for that resource type. Detected by correlating CloudTrail with GitHub PR and Terraform Cloud evidence."
            onClick={() => navigate('/lens/changes')}
          />
        </div>
        <div className={metricOrder(role, 'debt')} data-metric="debt">
          <MetricCard
            testId="metric-debt"
            label="Governance Debt"
            value={ps.governance_debt_usd / 1000}
            formatValue={(n) => Math.round(n).toLocaleString()}
            prefix="$"
            suffix="K"
            tone="slate"
            trend={ps.governance_debt_trend_pct}
            trendLabel={`${ps.governance_debt_trend_pct}% this quarter`}
            methodology="Formula: Control Gap Severity × Asset Criticality × Exposure Duration × Data Sensitivity × Observed Activity × Recurrence Rate. Industry benchmark: $2.1M average for organizations of this size. Your debt is 40% below benchmark."
            onClick={() => navigate('/lens/debt')}
          />
        </div>
      </div>

      {/* Signature visualization — Governance Graph hero CTA */}
      <motion.button
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        onClick={() => setGraphOpen(true)}
        data-testid="open-governance-graph"
        className="w-full mt-2 rounded-xl border-2 border-dashed border-apex-lens/30 bg-apex-lens/[0.03] hover:bg-apex-lens/[0.06] hover:border-apex-lens/50 transition-all p-4 flex items-center justify-between group"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-apex-lens/10 text-apex-lens flex items-center justify-center">
            <GitGraph className="w-4 h-4" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-apex-ink">Temporal Governance Graph</div>
            <div className="text-[11px] text-apex-neutral">
              INV-2026-042 · 7 entities · 6 relationships · All edges from CloudTrail evidence
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-semibold text-apex-lens group-hover:gap-3 transition-all">
          Open graph <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </motion.button>

      {/* Role emphasis hint */}
      <RoleEmphasis role={role} />

      {/* How this is calculated — score transparency */}
      <HowCalculated storageKey="command-readiness" testId="how-calculated-readiness" label="How Evidence Readiness is calculated">
        <ul className="space-y-2">
          <li className="flex items-baseline gap-2"><span className="text-apex-lens font-mono text-[11px] shrink-0">40%</span><span><strong className="text-apex-ink">Continuous control coverage.</strong> Share of in-scope controls where every required signal arrived within its expected interval over the last 30 days.</span></li>
          <li className="flex items-baseline gap-2"><span className="text-apex-lens font-mono text-[11px] shrink-0">35%</span><span><strong className="text-apex-ink">Approved-change baseline match.</strong> Share of production changes whose actor + path matched the documented baseline (Terraform Cloud, GitHub PRs, Change Calendar).</span></li>
          <li className="flex items-baseline gap-2"><span className="text-apex-lens font-mono text-[11px] shrink-0">25%</span><span><strong className="text-apex-ink">Documented exposure windows.</strong> Share of detected gaps with a corresponding incident, ticket, or risk acceptance recorded within SLA.</span></li>
        </ul>
        <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 gap-2 text-[11px] font-mono">
          <span className="text-apex-neutral">Window</span><span className="text-right">14 months CloudTrail</span>
          <span className="text-apex-neutral">Event coverage</span><span className="text-right">98.7%</span>
          <span className="text-apex-neutral">Last computed</span><span className="text-right">4 minutes ago</span>
          <span className="text-apex-neutral">Confidence</span><span className="text-right text-apex-lens">High</span>
        </div>
      </HowCalculated>

      {/* Two-column main */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Exposure Windows */}
        <div className="lg:col-span-1 order-2 lg:order-1">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-bold text-apex-ink">Active Exposure Windows</h2>
                <p className="text-[11px] text-apex-neutral">5 controls · 14 months of enforcement history</p>
              </div>
              <Activity className="w-4 h-4 text-apex-lens" />
            </div>
            <div className="space-y-4">
              {seed.exposure_windows.map((c, i) => {
                const gaps = c.gaps > 0
                  ? Array.from({ length: c.gaps }).map((_, j) => {
                      const span = (1 - c.enforcement_pct / 100) / c.gaps;
                      const start = 0.1 + j * 0.18 + j * span;
                      return { start, end: Math.min(start + span, 0.97), days: c.longest_days, events: Math.ceil(c.events_during_gap / c.gaps) };
                    })
                  : [];
                return (
                  <TimelineBar
                    key={i}
                    label={c.control}
                    enforcementPct={c.enforcement_pct}
                    gaps={gaps}
                    framework={c.framework}
                    testId={`exposure-${i}`}
                  />
                );
              })}
            </div>
          </div>

          {/* Framework coverage tiles */}
          <div className="mt-5 grid grid-cols-2 gap-3">
            {seed.framework_coverage.map((f) => (
              <motion.div
                key={f.name}
                whileHover={{ y: -2 }}
                className={`bg-white rounded-lg border border-slate-200 border-l-4 ${FRAMEWORK_COLORS[f.name] ?? ''} p-3`}
              >
                <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">{f.name}</div>
                <div className="text-xl font-bold tabular mt-0.5">{f.coverage_pct}%</div>
                <div className="text-[11px] text-apex-neutral">{f.gaps} gap{f.gaps !== 1 ? 's' : ''}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Pattern Intelligence */}
        <div className="lg:col-span-2 order-1 lg:order-2" id="pattern-intelligence" data-testid="pattern-intelligence">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-bold text-apex-ink">Pattern Intelligence</h2>
              <p className="text-[11px] text-apex-neutral">Conclusions, not events. Each statement is evidence-backed.</p>
            </div>
          </div>
          <div className="space-y-3">
            {seed.pattern_intelligence.map((p) => (
              <GovernanceStatement
                key={p.id}
                testId={`pattern-${p.id}`}
                severity={p.severity}
                statement={p.statement}
                evidenceCount={p.evidence_count}
                highlighted={p.investigation === 'INV-2026-042'}
                onCardClick={() => setPatternDrawer(p.id)}
                onPrimary={() => setPatternDrawer(p.id)}
                primaryLabel={p.action}
                secondaryLabel={p.investigation ? p.investigation : null}
                onSecondary={p.investigation ? () => navigate(`/lens/investigations?id=${p.investigation}`) : null}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Active investigations */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-apex-ink">Active Investigations</h2>
          <button onClick={() => navigate('/lens/investigations')} className="text-xs text-apex-lens font-semibold inline-flex items-center gap-1">
            View all <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {seed.investigations.map((inv) => (
            <button
              key={inv.id}
              data-testid={`active-inv-${inv.id}`}
              onClick={() => navigate(`/lens/investigations?id=${inv.id}`)}
              className="text-left bg-white rounded-lg border border-slate-200 p-4 hover:border-apex-lens hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <span className="text-[10px] font-mono text-apex-neutral">{inv.id}</span>
                <SeverityBadge severity={inv.severity} size="sm" />
              </div>
              <p className="text-sm font-semibold text-apex-ink leading-snug">{inv.title}</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-[11px] text-apex-neutral">{inv.days_open} days · {inv.team}</p>
                {inv.id === 'INV-2026-042' && <InvestigationBadge id={inv.id} />}
              </div>
            </button>
          ))}
        </div>

        {/* INV-2026-042 cinematic shortcut */}
        <button
          onClick={() => navigate('/lens/investigations?id=INV-2026-042&tab=overview')}
          data-testid="watch-replay-shortcut"
          className="w-full mt-3 text-[12px] font-semibold text-apex-lens hover:text-teal-600 transition-colors inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-apex-lens/30 bg-apex-lens/[0.04] hover:bg-apex-lens/[0.08] hover:border-apex-lens/50"
        >
          <Play className="w-3.5 h-3.5" />
          Watch incident replay — INV-2026-042 contamination story
          <ArrowRight className="w-3.5 h-3.5 ml-auto" />
        </button>
      </div>
      {/* Pattern Intelligence drawer */}
      <RecordDrawer
        open={!!patternDrawer}
        onOpenChange={(o) => !o && setPatternDrawer(null)}
        record={patternDrawer ? buildPatternRecord(patternDrawer, navigate) : null}
        testId="pattern-drawer"
      />

      {/* Full-viewport Governance Graph modal */}
      <AnimatePresence>
        {graphOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9000] bg-apex-shell/95 backdrop-blur-sm flex flex-col"
            data-testid="governance-graph-modal"
          >
            <span data-testid="graph-modal-esc-listener" className="sr-only">Escape closes</span>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-apex-lens/15 text-apex-lens flex items-center justify-center">
                  <GitGraph className="w-3.5 h-3.5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">Temporal Governance Graph</div>
                  <div className="text-[10px] text-slate-400 font-mono">
                    INV-2026-042 · Stratum Financial Group · All relationships derived from CloudTrail
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/lens/investigations?id=INV-2026-042')}
                  className="text-[11px] font-semibold text-apex-lens hover:text-teal-300 transition-colors"
                  data-testid="graph-modal-open-inv"
                >
                  Open full investigation →
                </button>
                <button
                  onClick={() => setGraphOpen(false)}
                  data-testid="graph-modal-close"
                  className="w-7 h-7 rounded-md border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 flex items-center justify-center transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden p-6">
              <GovernanceGraphFull />
            </div>
            <div className="px-6 py-3 border-t border-slate-700 flex flex-wrap items-center justify-between gap-3 text-[10px] text-slate-400 font-mono shrink-0">
              <span>Click any node to investigate · Hover edges for CloudTrail event reference</span>
              <div className="flex items-center gap-4">
                <span>847 CloudTrail events analyzed</span>
                <span>Evidence ID: EVD-2026-042-reconstruction</span>
                <span>SHA-256: b4e7c2d1...</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function buildPatternRecord(key, navigate) {
  const records = {
    'PAT-03': {
      idTag: 'PAT-03',
      title: 'Dev-origin image in 2 production Fargate services',
      severity: 'critical',
      subtitle: 'Behavioral pattern · detected across stratum-payments-prod and stratum-banking-prod',
      verdict: {
        tone: 'critical',
        label: 'Supply-chain trust boundary crossed',
        sub: 'Two production Fargate services are executing container images that APEX traced to a developer identity in the dev-sandbox account. Neither image has a corresponding production pipeline deployment event.',
      },
      signal: {
        tone: 'critical',
        lead: 'Why this is a governance failure, not just a risk:',
        body: 'A dev-origin image in production means the production change control process was bypassed. Every CVE in that image, every permission it carries, and every dependency it includes arrived without peer review, security scan, or approval. The pipeline exists to prevent exactly this.',
      },
      meta: [
        { label: 'Accounts affected', value: '2', mono: true, tone: 'critical', sub: 'stratum-payments-prod, stratum-banking-prod' },
        { label: 'First detected', value: 'Feb 14, 2026', mono: true, sub: '184 days ago' },
        { label: 'Evidence events', value: '47', mono: true, sub: 'CloudTrail correlated' },
        { label: 'PCI data exposure', value: 'Confirmed', mono: true, tone: 'critical', sub: 'Macie classification active' },
      ],
      sections: [
        {
          title: 'Detection timeline',
          type: 'chart',
          chart: {
            kind: 'progress',
            steps: [
              { label: 'Trust path created', sub: 'Oct 3, 2025', state: 'done' },
              { label: 'Dev image pushed',  sub: 'Nov 28, 2025', state: 'done' },
              { label: 'Deployed to prod',  sub: 'Feb 14, 2026', state: 'done' },
              { label: 'Traversal detected', sub: 'Apr 14, 2026', state: 'done' },
              { label: 'Remediation',       sub: 'Pending',      state: 'active' },
            ],
          },
        },
        {
          title: 'Audit translation',
          type: 'list',
          items: [
            'SOC 2 CC8.1 — change management control gap. Production change without evidence of approval.',
            'PCI DSS 6.3 — insecure development practice. Dev artifact in PCI-scoped production environment.',
            'ISO 27001 A.12.1.2 — change control procedure bypassed.',
          ],
        },
      ],
      actions: [
        { label: 'Open INV-2026-042', primary: true, onClick: () => navigate('/lens/investigations?id=INV-2026-042'), testId: 'pattern-open-inv' },
        { label: 'Open Container Governance', onClick: () => navigate('/lens/containers'), testId: 'pattern-open-cg' },
      ],
    },
    'PAT-02': {
      idTag: 'PAT-02',
      title: 'Lambda role called IAM actions for first time in 180 days',
      severity: 'high',
      subtitle: 'First-seen behavioral event · lambda-payments-processor · Apr 14 02:14 UTC',
      verdict: {
        tone: 'warning',
        label: 'Privilege escalation attempt',
        sub: 'iam:CreatePolicyVersion has never been called by this identity in 14 months of history. The timing — 02:14 UTC on April 14 — coincides exactly with the cross-environment trust traversal event in INV-2026-042.',
      },
      signal: {
        tone: 'critical',
        lead: 'Correlated with INV-2026-042:',
        body: 'This IAM action occurred 3 minutes after the successful AssumeRole traversal from the dev sandbox. The sequence — AssumeRole → IAM action — is consistent with privilege escalation behavior.',
      },
      meta: [
        { label: 'Identity', value: 'lambda-payments-processor', mono: true, sub: 'Click to inspect identity', pivot: { to: '/lens/identity?open=lambda-payments-processor', label: 'Open Identity Proof for lambda-payments-processor' } },
        { label: 'Action', value: 'iam:CreatePolicyVersion', mono: true, tone: 'critical' },
        { label: 'Time', value: 'Apr 14, 2026 02:14 UTC', mono: true },
        { label: 'Prior occurrence', value: 'Never in 14 months', mono: true, tone: 'critical' },
      ],
      sections: [
        {
          title: 'CloudTrail evidence',
          type: 'kv',
          items: [
            { k: 'eventName', v: 'CreatePolicyVersion' },
            { k: 'userIdentity.arn', v: 'arn:aws:iam::123:role/lambda-payments-processor', pivot: { to: '/lens/identity?open=lambda-payments-processor', label: 'Open Identity Proof' } },
            { k: 'eventTime', v: '2026-04-14T02:14:32Z' },
            { k: 'sourceIPAddress', v: 'AWS Internal' },
            { k: 'requestParameters.policyArn', v: 'arn:aws:iam::123:policy/payments-access' },
          ],
        },
      ],
      actions: [
        { label: 'Open Identity Proof', primary: true, onClick: () => navigate('/lens/identity'), testId: 'pattern-open-id' },
        { label: 'Open INV-2026-042', onClick: () => navigate('/lens/investigations?id=INV-2026-042'), testId: 'pattern-open-inv' },
      ],
    },
    'PAT-01': {
      idTag: 'PAT-01',
      title: 'Developer identity made 3 production changes without pipeline involvement',
      severity: 'high',
      subtitle: '7-day pattern · stratum-payments-prod · CloudTrail-correlated',
      verdict: {
        tone: 'warning',
        label: 'Out-of-pipeline production changes',
        sub: 'Three console-driven changes touched production resources within a 7-day window. No matching PR, reviewer, or pipeline deployment event was found in the change calendar.',
      },
      signal: {
        tone: 'warning',
        lead: 'Audit translation:',
        body: 'Each change is a documented exception against SOC 2 CC8.1. Until a Risk Acceptance is filed or the changes are reverted to the approved baseline, this counts toward the open governance debt.',
      },
      meta: [
        { label: 'Window', value: 'Last 7 days', mono: true },
        { label: 'Changes', value: '3', mono: true, tone: 'critical' },
        { label: 'Channel', value: 'AWS Console', tone: 'critical', sub: 'Evidence-light' },
        { label: 'Evidence events', value: '12', mono: true },
      ],
      sections: [
        {
          title: 'Recommended next step',
          type: 'list',
          items: [
            'Open Change Legitimacy and inspect the actor and channel for each event.',
            'Either revert to the approved baseline or file a Risk Acceptance for each change.',
            'Add a guardrail (SCP / OPA policy) so console actions cannot land on PCI-scoped resources.',
          ],
        },
      ],
      actions: [
        { label: 'Open Change Legitimacy', primary: true, onClick: () => navigate('/lens/changes'), testId: 'pattern-open-changes' },
      ],
    },
    'PAT-04': {
      idTag: 'PAT-04',
      title: 'Config evaluations stopped in stratum-reporting-prod',
      severity: 'high',
      subtitle: '9-day silence · No disable event found in CloudTrail',
      verdict: {
        tone: 'warning',
        label: 'Heartbeat dropped',
        sub: 'AWS Config has not emitted an evaluation event for stratum-reporting-prod in 9 days. The control is presumed dark — every day adds to the exception register.',
      },
      signal: {
        tone: 'warning',
        lead: 'What auditors will ask:',
        body: 'Either the control is broken, or the control was disabled without an authorized change. Both outcomes need a Risk Acceptance or remediation. APEX caught it before the auditor did.',
      },
      meta: [
        { label: 'Account', value: 'stratum-reporting-prod', mono: true },
        { label: 'Time since last beat', value: '9d', mono: true, tone: 'critical' },
        { label: 'Evidence events', value: '6', mono: true },
        { label: 'Disable event', value: 'Not found', tone: 'critical', mono: true },
      ],
      sections: [
        {
          title: 'Path to closure',
          type: 'list',
          items: [
            'Restore the AWS Config delivery channel or document the intentional pause as a Risk Acceptance.',
            'Re-run the control validator to close the heartbeat gap automatically.',
          ],
        },
      ],
      actions: [
        { label: 'Open Missing Evidence', primary: true, onClick: () => navigate('/lens/missing-evidence'), testId: 'pattern-open-me' },
      ],
    },
    'PAT-05': {
      idTag: 'PAT-05',
      title: 'Same CVE present in 47 images — one base-image update closes all',
      severity: 'medium',
      subtitle: 'Supply-chain leverage · 47 derived images share one base layer',
      verdict: {
        tone: 'improving',
        label: 'High-leverage fix',
        sub: 'One base-image bump closes the CVE across 47 derived images simultaneously. Effort is small; the audit packet will reflect every closure on the next evidence cycle.',
      },
      signal: {
        tone: 'improving',
        lead: 'Why act on this first:',
        body: 'Closing this single base layer reduces the open vulnerability surface by 47 — the highest leverage remediation in the queue this week.',
      },
      meta: [
        { label: 'Affected images', value: '47', mono: true },
        { label: 'CVE', value: 'CVE-2025-XXXX', mono: true },
        { label: 'Base image', value: 'stratum-base-layer:2.1', mono: true },
        { label: 'Estimated effort', value: '20 min', mono: true, tone: 'success' },
      ],
      sections: [],
      actions: [
        { label: 'Open Container Governance', primary: true, onClick: () => navigate('/lens/containers'), testId: 'pattern-open-cg' },
      ],
    },
  };
  return records[key] ?? null;
}

function Loading() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
    </div>
  );
}

// Role-driven metric ordering
const ROLE_METRIC_ORDER = {
  CISO:        { readiness: 1, debt: 0, gaps: 2, changes: 3 },
  Security:    { changes: 0, gaps: 1, readiness: 2, debt: 3 },
  Engineering: { changes: 0, debt: 1, gaps: 2, readiness: 3 },
  Audit:       { readiness: 0, gaps: 1, debt: 2, changes: 3 },
};
function metricOrder(role, key) {
  const n = ROLE_METRIC_ORDER[role]?.[key] ?? 0;
  return ['order-1', 'order-2', 'order-3', 'order-4'][n];
}

const ROLE_VIEW = {
  CISO: { label: 'Boardroom view', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  Security: { label: 'SOC view', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  Engineering: { label: 'Workload view', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  Audit: { label: 'Audit view', color: 'bg-violet-100 text-violet-700 border-violet-200' },
};
function RoleViewLabel({ role }) {
  const v = ROLE_VIEW[role] ?? ROLE_VIEW.CISO;
  return (
    <span data-testid={`role-view-label-${role.toLowerCase()}`} className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold border rounded-full px-2 py-0.5 ${v.color}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {v.label} · {role}
    </span>
  );
}

const ROLE_EMPHASIS = {
  CISO: { tag: 'Boardroom view', text: 'Governance debt trend, framework readiness, exposure window history.' },
  Security: { tag: 'SOC view', text: 'Pattern intelligence, active investigations, missing-evidence alerts.' },
  Engineering: { tag: 'Workload view', text: 'Remediation queue, container cleanup candidates, pipeline bypasses.' },
  Audit: { tag: 'Audit view', text: 'Evidence Vault recent activity, reconstruction shortcuts, framework control gaps.' },
};

function RoleEmphasis({ role }) {
  const e = ROLE_EMPHASIS[role] ?? ROLE_EMPHASIS.CISO;
  return (
    <div data-testid={`role-emphasis-${role.toLowerCase()}`} className="flex flex-wrap items-center gap-2 text-[11px] text-apex-neutral">
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-apex-lens/10 text-apex-lens border border-apex-lens/20 font-semibold uppercase tracking-wider text-[10px]">
        <span className="w-1.5 h-1.5 rounded-full bg-apex-lens" />
        {e.tag}
      </span>
      <span>{e.text}</span>
    </div>
  );
}
